/* eslint-disable no-console */
import { CHATKIT_CONFIGS } from './configs';
import { extractContextFromDataset } from '../common/helpers';
import { loadChatkitScript } from './scriptLoader';
import { updateChatkitConversation, fetchToken, refreshToken } from './apiRoutes';
import { touchChatkitConversation } from '../common/apiRoutes';
import * as crossTabSync from '../common/crossTabSync';
import { CONNECTING_TO_LIVE_AGENT_MESSAGE } from '../aws/awsWidget';

let _chatkitContainerSelector = '#chatkit-widget-container';
let _widgetInitialized = false;
let _flowType = null;
let _chatkitThreadId = null;

function _getConfig(container) {
  const type = container.dataset.chatkitType || _flowType;
  const config = { ...CHATKIT_CONFIGS[type] };

  const dynamicContext = extractContextFromDataset(container);
  if (Object.keys(dynamicContext).length > 0) {
    config.context = { ...config.context, ...dynamicContext };
  }

  return config;
}

async function _prepareConfigAndToken(container) {
  if (container.dataset.chatkitType) {
    const config = _getConfig(container);
    const tokenData = await fetchToken(_chatkitThreadId, config.context);
    return { config, tokenData };
  }

  const dynamicContext = extractContextFromDataset(container);
  const tokenData = await fetchToken(_chatkitThreadId, dynamicContext);
  if (!tokenData) return null;

  _flowType = tokenData.flowType;
  const config = _getConfig(container);
  return { config, tokenData };
}

function _showComposerOverlay(content) {
  const overlay = document.querySelector(`${_chatkitContainerSelector} .openai-chatkit-composer-overlay`);
  if (!overlay) return;
  overlay.replaceChildren(content);
  overlay.classList.remove('d-none');
  overlay.focus();
}

function _hideComposerOverlay() {
  const overlay = document.querySelector(`${_chatkitContainerSelector} .openai-chatkit-composer-overlay`);
  if (!overlay) return;
  overlay.classList.add('d-none');
  overlay.replaceChildren();
}

async function _initialize(chatkitThreadId = null) {
  _chatkitThreadId = chatkitThreadId;
  await loadChatkitScript();

  const container = document.querySelector(_chatkitContainerSelector);
  const chatkit = container.querySelector('openai-chatkit');
  if (!chatkit) return;

  if (chatkit.shadowRoot && chatkit.shadowRoot.querySelector('iframe')) {
    _widgetInitialized = true;
    return;
  }

  const result = await _prepareConfigAndToken(container);
  const { config, tokenData } = result;

  try {
    const userSettings = document.querySelector('[data-controller="user-account-settings"]');
    const appearanceMode = userSettings?.dataset?.userAccountSettingsThemeValue || 'light';

    const options = {
      api: {
        async getClientSecret(refreshSession) {
          if (refreshSession) {
            const newTokenData = await refreshToken(config.context);
            if (newTokenData) return newTokenData.token;
            throw new Error('Failed to refresh ChatKit token');
          }
          return tokenData.token;
        }
      },
      initialThread: _chatkitThreadId,
      theme: {
        colorScheme: appearanceMode
      },
      composer: {
        placeholder: config.placeholder,
        attachments: config.attachments
      },
      startScreen: {
        greeting: config.greeting,
        prompts: config.prompts
      }
    };

    chatkit.setOptions(options);
    crossTabSync.setup();

    chatkit.addEventListener('chatkit.response.start', () => {
      document.dispatchEvent(new CustomEvent('chatkit.response.start', { bubbles: true }));
      crossTabSync.broadcast('chatkit.sync.response.start');
      touchChatkitConversation();
    });

    chatkit.addEventListener('chatkit.response.end', () => {
      document.dispatchEvent(new CustomEvent('chatkit.response.end', { bubbles: true }));
      crossTabSync.broadcast('chatkit.sync.response.end');
    });

    chatkit.addEventListener('chatkit.thread.change', async (event) => {
      _chatkitThreadId = event.detail.threadId;
      await updateChatkitConversation(_chatkitThreadId, _flowType);
      crossTabSync.broadcast('chatkit.sync.thread.change', { threadId: _chatkitThreadId });
    });

    document.addEventListener('chatkit.live-agent-request', () => {
      const content = document.createElement('div');
      content.className = 'aws-chatkit-event';
      content.textContent = CONNECTING_TO_LIVE_AGENT_MESSAGE;
      _showComposerOverlay(content);
    });

    document.addEventListener('chatkit.sync.response.start', () => {
      setTimeout(() => {
        if (chatkit.fetchUpdates) chatkit.fetchUpdates();
        document.dispatchEvent(new CustomEvent('chatkit.response.start', { bubbles: true }));
      }, 200);
    });

    document.addEventListener('chatkit.sync.response.end', () => {
      setTimeout(() => {
        if (chatkit.fetchUpdates) chatkit.fetchUpdates();
        document.dispatchEvent(new CustomEvent('chatkit.response.end', { bubbles: true }));
      }, 1000);
    });

    document.addEventListener('chatkit.sync.thread.change', (event) => {
      const { threadId } = event.detail;
      _chatkitThreadId = threadId;
      chatkit.setThreadId(threadId);
    });
  } catch (error) {
    console.error('Error initializing ChatKit:', error);
  }

  _widgetInitialized = true;
}

export function activate(chatkitThreadId = null, chatkitContainerSelector = null) {
  if (chatkitContainerSelector) _chatkitContainerSelector = chatkitContainerSelector;
  const chatkit = document.querySelector(`${_chatkitContainerSelector} openai-chatkit`);
  if (!chatkit) return;
  chatkit.classList.remove('d-none');
  _hideComposerOverlay();

  if (!_widgetInitialized) {
    _initialize(chatkitThreadId);
  }
}

export function deactivate() {
  const chatkit = document.querySelector(`${_chatkitContainerSelector} openai-chatkit`);
  if (!chatkit) return;
  chatkit.classList.add('d-none');
  _hideComposerOverlay();
}

export function destroy() {
  const chatkit = document.querySelector(`${_chatkitContainerSelector} openai-chatkit`);
  if (chatkit) {
    if (chatkit.shadowRoot) chatkit.shadowRoot.innerHTML = '';
    if (chatkit.parentNode) {
      const fresh = chatkit.cloneNode(false);
      chatkit.parentNode.replaceChild(fresh, chatkit);
    }
  }

  crossTabSync.cleanup();
  _widgetInitialized = false;
}
